// Loader for `config/tenants.json` — the committed tenantId -> {brand, appUrl}
// map (ADR-0010). One config, three roles: runtime brand map, tenant ALLOWLIST
// (unknown tenants are rejected — fail closed, known gaps N1/N2), and
// back-to-login targets (appUrl is the IAP-protected app entry, so IAP starts
// a fresh login redirect).
//
// Safe to commit: tenant ids/app URLs are public (they appear in every reset
// email link and IAP redirect); auth is enforced server-side by GCIP/IAP.
// Stage and prod entries live side by side — tenant ids are unique per
// project, so the id itself selects the environment (same pattern as the
// PROJECTS map in src/auth.ts). This keeps the bundle env-agnostic: a verified
// stage deploy is promoted to prod as-is, no rebuild.
//
// INVARIANT: creating a GCIP tenant and adding its entry here happen together —
// a tenant missing from the config cannot sign in. See docs/add-a-new-brand.md.

import { z } from "zod";
import raw from "../../../config/tenants.json";

const tenantConfigSchema = z.object({
  // Extend together with the `Brand` type in brand.ts.
  brand: z.enum(["onecareer", "chapternext"]),
  appUrl: z.url(),
});

const tenantsFileSchema = z.object({
  tenants: z.record(z.string(), tenantConfigSchema),
});

export type TenantConfig = z.infer<typeof tenantConfigSchema>;

// Parse at module load: a malformed config fails loud at boot.
const TENANTS: Readonly<Record<string, TenantConfig>> =
  tenantsFileSchema.parse(raw).tenants;

export function tenantConfigFor(
  tenantId: string | null | undefined,
): TenantConfig | null {
  if (!tenantId) return null;
  // Own entries only: a plain object also answers for "constructor" or
  // "__proto__" from Object.prototype, and a link carrying one of those as
  // tenantId must land on the error screen, not pass the allowlist.
  return Object.hasOwn(TENANTS, tenantId) ? TENANTS[tenantId] : null;
}

/** Allowlist check for the auth flows (fail closed on unknown tenants). */
export function isAllowedTenant(tenantId: string | null | undefined): boolean {
  return tenantConfigFor(tenantId) !== null;
}

/** IAP-protected app URL for "back to login"; null for unknown tenants. */
export function appUrlFor(tenantId: string | null | undefined): string | null {
  return tenantConfigFor(tenantId)?.appUrl ?? null;
}

let cachedTenantId: string | null | undefined;

/**
 * Tenant id from the current URL: `tenantId` (email action links) or `tid`
 * (IAP sign-in redirect). Cached, since gcip-iap mutates window.location
 * during the handshake.
 */
export function readTenantIdFromUrl(): string | null {
  if (cachedTenantId !== undefined) return cachedTenantId;
  const params = new URLSearchParams(window.location.search);
  cachedTenantId = params.get("tenantId") ?? params.get("tid");
  return cachedTenantId;
}
